import React, { useCallback, useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';

export default function AdminOrdersPage() {
  const { token } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/orders', {
        headers: { 'x-auth-token': token },
      });
      if (res.ok) setOrders(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const res = await fetch(`/api/orders/${orderId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Unable to update order status.');
      loadOrders();
    } catch (err) {
      console.error(err);
    }
  };

  const openInvoice = async (orderId) => {
    const invoiceWindow = window.open('', '_blank');
    if (!invoiceWindow) {
      alert('Please allow pop-ups to view the invoice.');
      return;
    }
    setInvoiceLoadingId(orderId);
    try {
      const res = await fetch(`/api/orders/${orderId}/invoice`, {
        headers: { 'x-auth-token': token },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.msg || 'Unable to generate invoice.');
      }
      const blobUrl = URL.createObjectURL(await res.blob());
      invoiceWindow.location.href = blobUrl;
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
    } catch (error) {
      invoiceWindow.close();
      alert(error.message);
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  return (
    <div className="container-fluid py-4">
      <h2 className="brand-font mb-4">Order Management & Fulfillment</h2>

      {loading ? (
        <div className="spinner-border text-primary" role="status"></div>
      ) : (
        <div className="border rounded bg-card p-3 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Fulfillment Status</th>
                  <th>Date</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td className="font-weight-bold">{o.order_number}</td>
                    <td>
                      <div>{o.customer_name}</div>
                      <div className="small text-muted">{o.customer_email}</div>
                    </td>
                    <td className="font-weight-bold text-primary">₹{o.total_amount}</td>
                    <td>
                      <span className="badge bg-secondary">{o.payment_status}</span>
                    </td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={o.status}
                        onChange={(e) => handleUpdateStatus(o.id, e.target.value)}
                        style={{ width: '140px' }}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Packed">Packed</option>
                        <option value="Shipped">Shipped</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="small text-muted">{new Date(o.date).toLocaleDateString()}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openInvoice(o.id)}
                        disabled={invoiceLoadingId === o.id || o.status !== 'Delivered'}
                      >
                        {invoiceLoadingId === o.id ? 'Preparing…' : o.status === 'Delivered' ? 'View invoice' : 'Invoice after delivery'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
